<script setup lang="ts">
import { computed } from 'vue';
import { useKeyboardShortcuts, formatKeyForDisplay } from '@/composables/useKeyboardShortcuts';

const { showHelp, getShortcutsByCategory } = useKeyboardShortcuts();

const groupedShortcuts = computed(() => getShortcutsByCategory());

function closeModal() {
  showHelp.value = false;
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="showHelp" class="shortcuts-overlay" @mousedown.self="closeModal">
        <div class="shortcuts-modal">
          <div class="shortcuts-header">
            <h4>
              <i class="bi bi-keyboard me-2"></i>
              Keyboard Shortcuts
            </h4>
            <button class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>

          <div class="shortcuts-body">
            <div v-if="Object.keys(groupedShortcuts).length === 0" class="text-muted text-center py-4">
              No shortcuts registered yet.
            </div>

            <div v-else class="shortcuts-grid">
              <div
                v-for="(categoryShortcuts, category) in groupedShortcuts"
                :key="category"
                class="shortcut-category"
              >
                <h6 class="category-title">{{ category }}</h6>
                <div class="shortcut-list">
                  <div
                    v-for="shortcut in categoryShortcuts"
                    :key="shortcut.key"
                    class="shortcut-item"
                    :class="{ disabled: shortcut.enabled === false }"
                  >
                    <span class="shortcut-description">{{ shortcut.description }}</span>
                    <kbd class="shortcut-key">{{ formatKeyForDisplay(shortcut.key) }}</kbd>
                  </div>
                </div>
              </div>
            </div>

            <div class="shortcuts-footer">
              <span class="text-muted small">
                Press <kbd>?</kbd> to toggle this help
              </span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.shortcuts-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: 20px;
}

.shortcuts-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shortcuts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.shortcuts-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.shortcuts-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.shortcuts-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.shortcut-category {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.category-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.shortcut-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.shortcut-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.15s;
}

.shortcut-item:hover {
  background: #f8f9fa;
}

.shortcut-item.disabled {
  opacity: 0.5;
}

.shortcut-description {
  font-size: 14px;
  color: #495057;
}

.shortcut-key {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f1f3f4;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

.shortcuts-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.shortcuts-footer kbd {
  padding: 2px 6px;
  background: #f1f3f4;
  border: 1px solid #dadce0;
  border-radius: 3px;
  font-size: 11px;
}

/* Transition animations */
.modal-enter-active {
  animation: modal-in 0.2s ease-out;
}

.modal-leave-active {
  animation: modal-out 0.15s ease-in forwards;
}

.modal-enter-active .shortcuts-modal {
  animation: modal-content-in 0.2s ease-out;
}

.modal-leave-active .shortcuts-modal {
  animation: modal-content-out 0.15s ease-in forwards;
}

@keyframes modal-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modal-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes modal-content-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modal-content-out {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
}

</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .shortcuts-overlay {
  background: rgba(0, 0, 0, 0.7);
}

[data-bs-theme="dark"] .shortcuts-modal {
  background: #1e293b;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

[data-bs-theme="dark"] .shortcuts-header {
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .shortcuts-header h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .category-title {
  color: #94a3b8;
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .shortcut-item:hover {
  background: #334155;
}

[data-bs-theme="dark"] .shortcut-description {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .shortcut-key {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .shortcuts-footer {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .shortcuts-footer kbd {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .shortcuts-modal .btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}
</style>
